import { supabase } from '@/lib/supabase/client'
import { uploadDocument } from '@/services/document-upload.service'
import { generatePDF as generateETPDF } from '@/services/etPDFTemplate'
import { DEFAULT_ET_PROBE_SETTINGS } from '@/types/etTypes'
import type { ETObjectType, ETReportData } from '@/types/etTypes'
import {
  STMIDBEquipmentService,
  STMIDB_WLL_COMPANY_ID,
  getSTMIDBUploadCode,
  mapWLLDocumentTypeToSTMIDB,
} from '@/services/api/stmidbEquipmentService'
import { getSTMIDBNdtCategoryCode } from '@/utils/ndtEquipmentReport'
import { isSlingCategory, matchesCategoryFamily } from '@/utils/categoryFamily'

const NDT_DATE_TEMPLATE_ID = 'd0dd8453-662a-49af-8571-0d8a5a1deb20'
const NDT_VALID_YEARS = 4

export const addYearsToIsoDate = (isoDate: string, years: number) => {
  const [year, month, day] = isoDate.split('-').map(Number)
  return `${year + years}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export const profileHasEt = (
  person: { et?: boolean | null; et_level?: number | null; hasEt?: boolean } | null | undefined,
) => {
  if (!person) return false
  if (typeof person.hasEt === 'boolean') return person.hasEt
  return person.et === true || (person.et !== false && !!person.et_level && person.et_level > 0)
}

export const inferETObjectType = (categoryCode: string | null | undefined, name: string) => {
  const code = (categoryCode || '').toUpperCase()
  if (matchesCategoryFamily(code, 'R1')) {
    return { objectType: 'Sling' as ETObjectType, customObjectType: undefined, isChainSling: true }
  }
  if (matchesCategoryFamily(code, 'R3')) {
    return { objectType: 'Sling' as ETObjectType, customObjectType: undefined, isChainSling: false }
  }
  if (code.startsWith('F') && !code.startsWith('FAL')) {
    const objectType: ETObjectType = name.toUpperCase().includes('ISO') ? 'POU w/ISO' : 'POU'
    return { objectType, customObjectType: undefined, isChainSling: false }
  }
  return { objectType: 'custom' as ETObjectType, customObjectType: name, isChainSling: false }
}

export const updateNdtDatesForEquipment = async (equipmentId: string, performedDate: string, isSTMIDB = false) => {
  if (isSTMIDB) {
    // set_stmidb_ndt_date also sets STMIDB's next_ndt_date (48M DUE).
    try {
      await STMIDBEquipmentService.setNDTDate(equipmentId, performedDate)
    } catch (err) {
      console.error('Failed to update SLB NDT date:', err)
    }
    return
  }

  const { error: colError } = await supabase
    .from('equipment_v2')
    .update({
      last_ndt_date: performedDate,
      next_ndt_date: addYearsToIsoDate(performedDate, NDT_VALID_YEARS),
    })
    .eq('id', equipmentId)

  if (colError) console.error('Failed to update NDT columns:', colError)

  const { data: existing, error: specFetchError } = await supabase
    .from('equipment_specifications_v2')
    .select('id')
    .eq('equipment_id', equipmentId)
    .in('spec_key', ['ndt_date', 'last_ndt_date'])

  if (specFetchError) {
    console.error('Failed to fetch NDT specs:', specFetchError)
    return
  }

  if (existing && existing.length > 0) {
    const { error: specUpdateError } = await supabase
      .from('equipment_specifications_v2')
      .update({
        value_date: performedDate,
        value_text: null,
        value_numeric: null,
        value_integer: null,
        value_boolean: null,
        value_reference_id: null,
      })
      .in('id', existing.map(s => s.id))

    if (specUpdateError) console.error('Failed to update NDT spec:', specUpdateError)
    return
  }

  const { data: { user } } = await supabase.auth.getUser()
  const { error: specInsertError } = await supabase
    .from('equipment_specifications_v2')
    .insert({
      equipment_id: equipmentId,
      template_id: NDT_DATE_TEMPLATE_ID,
      spec_key: 'ndt_date',
      value_date: performedDate,
      validation_status: 'pending',
      is_validated: false,
      version: 1,
      created_by: user?.id,
    })

  if (specInsertError) console.error('Failed to insert NDT spec:', specInsertError)
}

export interface AttachedSling {
  equipmentId: string
  serial: string
  isChain: boolean
}

export const fetchAttachedSlings = async (equipmentId: string, isSTMIDB = false): Promise<AttachedSling[]> => {
  if (isSTMIDB) {
    const children = await STMIDBEquipmentService.getSubEquipment(equipmentId)
    return children.flatMap(child => {
      const code = getSTMIDBNdtCategoryCode(child.category, child.equipment_type)
      const serial = child.serial_number || child.asset_number
      if (!isSlingCategory(code) || !serial) return []
      return [{ equipmentId: child.id, serial, isChain: matchesCategoryFamily(code, 'R1') }]
    })
  }

  const { data } = await supabase
    .from('equipment_relationship_v2')
    .select(`
      sub_equipment:equipment_v2!equipment_relationship_v2_sub_equipment_id_fkey(
        id,
        serial_number,
        asset_number,
        categories_v2(code)
      )
    `)
    .eq('main_equipment_id', equipmentId)

  const slings: AttachedSling[] = []
  for (const rel of data || []) {
    const sub = (Array.isArray(rel.sub_equipment) ? rel.sub_equipment[0] : rel.sub_equipment) as {
      id: string
      serial_number: string | null
      asset_number: string | null
      categories_v2: { code: string } | { code: string }[] | null
    } | null
    if (!sub) continue

    const category = Array.isArray(sub.categories_v2) ? sub.categories_v2[0] : sub.categories_v2
    const code = (category?.code || '').toUpperCase()
    if (!isSlingCategory(code)) continue

    const serial = sub.serial_number || sub.asset_number
    if (!serial) continue
    slings.push({ equipmentId: sub.id, serial, isChain: matchesCategoryFamily(code, 'R1') })
  }
  return slings
}

/** Renews the NDT date on the equipment and every sling attached to it. */
export const renewNdtDates = async (equipmentId: string, performedDate: string, isSTMIDB = false) => {
  const slings = await fetchAttachedSlings(equipmentId, isSTMIDB)
  const ids = Array.from(new Set([equipmentId, ...slings.map(s => s.equipmentId)]))
  await Promise.all(ids.map(id => updateNdtDatesForEquipment(id, performedDate, isSTMIDB)))
}

export interface EtCompany {
  id: string
  name: string
  report_code: string | null
}

export const fetchEtCompany = async (equipmentId: string, isSTMIDB = false): Promise<EtCompany | null> => {
  if (isSTMIDB) {
    const { data } = await supabase
      .from('companies')
      .select('id, name, report_code')
      .eq('id', STMIDB_WLL_COMPANY_ID)
      .single()
    return {
      id: STMIDB_WLL_COMPANY_ID,
      name: data?.name || 'SLB',
      report_code: data?.report_code ?? null,
    }
  }

  const { data: eqData } = await supabase
    .from('equipment_v2')
    .select('company_id, companies!inner(id, name, report_code)')
    .eq('id', equipmentId)
    .single()

  const companyRow = eqData?.companies as unknown
  const company = (Array.isArray(companyRow) ? companyRow[0] : companyRow) as EtCompany | null

  if (!company && !eqData?.company_id) return null

  return {
    id: company?.id || eqData?.company_id,
    name: company?.name || 'unknown',
    report_code: company?.report_code ?? null,
  }
}

export interface EtEquipment {
  id: string
  name: string
  serial_number: string | null
  asset_number: string | null
  owner_id?: string | null
  categoryCode?: string | null
}

export interface BuildEtReportInput {
  equipment: EtEquipment
  isSTMIDB: boolean
  company: EtCompany
  orderNo: string | null
  performedDate: string
  status: string
  remarks?: string | null
  issuedBy: string
  verifiedBy?: string | null
  attachedImages?: string[]
}

export const buildEtReport = async (input: BuildEtReportInput) => {
  const { equipment, company } = input
  const reportNumber =
    input.orderNo?.trim() || `I${new Date().toISOString().slice(2, 4)}${String(new Date().getMonth() + 1).padStart(2, '0')}`
  const serialNumber = equipment.serial_number || equipment.asset_number || 'N/A'
  const { objectType, customObjectType, isChainSling: selfIsChain } = inferETObjectType(
    equipment.categoryCode,
    equipment.name,
  )
  const isSling = objectType === 'Sling'
  const attachedSlings = isSling ? [] : await fetchAttachedSlings(equipment.id, input.isSTMIDB)

  let slingId: string | undefined
  let isChainSling = selfIsChain
  if (isSling) {
    slingId = serialNumber
  } else if (attachedSlings.length > 0) {
    slingId = attachedSlings.map(s => s.serial).join(' & ')
    isChainSling = attachedSlings.some(s => s.isChain)
  }

  const { data: existingFiles } = await supabase.storage.from('reports').list(reportNumber)
  const sequenceNumber = existingFiles ? existingFiles.length + 1 : 1
  const paddedSequence = String(sequenceNumber).padStart(3, '0')

  const etStatus = input.status === 'NC' ? 'Not Accepted' : 'Accepted'
  const etData: ETReportData = {
    testDate: input.performedDate,
    reportNumber,
    unitId: isSling ? '' : serialNumber,
    slingId,
    isChainSling,
    issuedBy: input.issuedBy,
    verifiedBy: input.verifiedBy ?? undefined,
    status: etStatus,
    objectType,
    customObjectType,
    probeSettings: DEFAULT_ET_PROBE_SETTINGS,
    certificationNumber: '',
    sequenceNumber: paddedSequence,
    material: 'Carbon steel',
    company: company.name,
    report_code: company.report_code,
    comments: input.remarks || undefined,
    attachedImages: (input.attachedImages ?? []).map((image, i) => ({ id: `inspection-attachment-${i}`, image })),
    approvedBy: input.verifiedBy ?? undefined,
    approvedAt: input.performedDate,
  }

  const pdfBlob = await generateETPDF(etData)
  const fileName = `${reportNumber}_${paddedSequence}.pdf`

  return { pdfBlob, etData, paddedSequence, fileName, folderPath: `${reportNumber}/${fileName}`, etStatus, serialNumber, reportNumber }
}

export interface SaveEtReportInput extends BuildEtReportInput {
  /** Profile id of the ordreansvarlig approving the report. */
  approvedById?: string | null
}

/** Builds the ET report, stores it, registers it in ndt_reports and attaches it to the equipment. */
export const saveEtReport = async (input: SaveEtReportInput): Promise<Blob> => {
  const { pdfBlob, etData, fileName, folderPath, etStatus, serialNumber, reportNumber, paddedSequence } =
    await buildEtReport(input)
  const { equipment, company } = input

  const { error: uploadError } = await supabase.storage
    .from('reports')
    .upload(folderPath, pdfBlob, { contentType: 'application/pdf', cacheControl: '3600' })

  if (uploadError) {
    console.error('ET report storage upload failed:', uploadError)
  }

  const { data: { user } } = await supabase.auth.getUser()
  const { error: metadataError } = await supabase.from('ndt_reports').insert({
    order_number: `ET-${reportNumber}.${paddedSequence}`,
    unit_id: serialNumber,
    test_date: input.performedDate,
    test_type: 'ET',
    status: etStatus,
    issued_by: input.issuedBy,
    verified_by: input.verifiedBy || null,
    file_path: folderPath,
    report_code: company.report_code,
    company_id: company.id,
    equipment_id: equipment.id,
    created_by: user?.id,
    approved_by: input.approvedById || null,
    approved_at: input.performedDate,
    report_data: etData,
  })

  if (metadataError) {
    console.error('ET report metadata insert failed:', metadataError)
  }

  const etFile = new File([pdfBlob], `ET-${reportNumber}.${paddedSequence}.pdf`, { type: 'application/pdf' })
  if (input.isSTMIDB) {
    await STMIDBEquipmentService.uploadDocument({
      equipmentId: equipment.id,
      equipmentCode: getSTMIDBUploadCode(equipment),
      documentType: mapWLLDocumentTypeToSTMIDB('ndt_report'),
      file: etFile,
    })
  } else {
    await uploadDocument({
      file: etFile,
      customerName: company.name,
      serialNumber,
      equipmentId: equipment.id,
      documentType: 'ndt_report',
    })
  }

  return pdfBlob
}
